import { useEffect } from "react";

/**
 * Toggles the platform-wide "Made with Emergent" badge from the bottom-right
 * of the screen.  Hidden when localStorage.hide_emergent_badge === "1".
 *
 * Mounted once at the root of <App/>, so it covers ALL routes including the
 * public product pages (`/p/:slug`), the login screen, and password reset —
 * not only the authenticated /app shell.  The MutationObserver re-applies
 * the rule if the Emergent script re-injects the badge after a client-side
 * route change.
 */
const STYLE_ID = "vendeia-hide-emergent-badge";

export default function EmergentBadgeGate() {
  useEffect(() => {
    let observer = null;

    const enforceHide = () => {
      const el = document.getElementById("emergent-badge");
      if (el) {
        el.style.setProperty("display", "none", "important");
        el.setAttribute("aria-hidden", "true");
      }
    };

    const apply = () => {
      const hide = localStorage.getItem("hide_emergent_badge") === "1";
      let styleEl = document.getElementById(STYLE_ID);
      if (hide) {
        if (!styleEl) {
          styleEl = document.createElement("style");
          styleEl.id = STYLE_ID;
          styleEl.textContent =
            "#emergent-badge, a#emergent-badge { display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; }";
          document.head.appendChild(styleEl);
        }
        enforceHide();
        if (!observer && typeof MutationObserver !== "undefined") {
          observer = new MutationObserver(enforceHide);
          observer.observe(document.body, { childList: true, subtree: true });
        }
      } else {
        if (styleEl) styleEl.remove();
        const el = document.getElementById("emergent-badge");
        if (el) {
          el.style.removeProperty("display");
          el.style.removeProperty("visibility");
          el.style.removeProperty("opacity");
          el.style.removeProperty("pointer-events");
          el.removeAttribute("aria-hidden");
        }
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }
    };

    apply();
    window.addEventListener("storage", apply);
    // Polite re-check after a tick so the badge script (injected via <script src>)
    // has time to attach before we hide it.
    const t1 = setTimeout(apply, 400);
    const t2 = setTimeout(apply, 1500);
    return () => {
      window.removeEventListener("storage", apply);
      clearTimeout(t1);
      clearTimeout(t2);
      if (observer) observer.disconnect();
    };
  }, []);

  return null;
}
